import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { usersAPI, type LearnedWord } from '@/services/api/users.api';
import { Spinner } from '@/components/atoms/Spinner';
import { Button } from '@/components/atoms/Button';
import { VocabCard } from '@/components/molecules/VocabCard';
import { Tabs, TabsContent } from '@/components/molecules/Tabs';
import { SegmentedTabsList } from '@/components/molecules/SegmentedTabs';
import { buildRoute, ROUTES } from '@/constants/routes';
import { getWeekStartMs } from '@/pages/student/ProgressPage/utils/wordPeriodRanges';
import { learnedWordToVocabItem } from '@/pages/student/ProgressPage/utils/learnedWordToVocabItem';
import { savedVocabularyToVocabItem } from '@/pages/student/LearnedWordsPages/utils/savedVocabularyToVocabItem';
import { useSavedVocabulary } from '@/context/SavedVocabularyContext';
import type { VocabItem } from '@/types/vocabulary.types';
import styles from './LearnedWordsPages.module.scss';

const WEEK_LIST_LIMIT = 100;
/** Tamaño de página del historial (GET learned-words); si la respuesta llega llena, hay más en servidor. */
const HISTORY_PAGE_SIZE = 40;

const SEGMENTS = ['learned', 'saved'] as const;
type LearnedWordsSegment = (typeof SEGMENTS)[number];

function isPeriod(v: string | undefined): v is 'week' | 'month' {
  return v === 'week' || v === 'month';
}

export function LearnedWordsListPage() {
  const { t } = useTranslation();
  const { period } = useParams<{ period: string }>();
  const location = useLocation();
  const [segment, setSegment] = useState<LearnedWordsSegment>('learned');
  const [wordsThisWeek, setWordsThisWeek] = useState<LearnedWord[]>([]);
  const [wordsAllTime, setWordsAllTime] = useState<LearnedWord[]>([]);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [isLoadingHistoryMore, setIsLoadingHistoryMore] = useState(false);
  const [loading, setLoading] = useState(true);

  const { savedItems, removeItem, isLoading: savedLoading } = useSavedVocabulary();

  const loadLearnedTab = useCallback(async () => {
    setLoading(true);
    setIsLoadingHistoryMore(false);
    const now = Date.now();
    const weekFrom = getWeekStartMs();
    try {
      const [weekRows, allFirst] = await Promise.all([
        // Solo palabras realmente nuevas esta semana: si el alumno ya las había
        // visto antes en otro historia, no se muestran aquí (aparecen en el historial).
        usersAPI.getLearnedWords({
          limit: WEEK_LIST_LIMIT,
          offset: 0,
          from: weekFrom,
          to: now,
          excludeSeenBefore: weekFrom,
        }),
        // Histórico = anteriores a esta semana. Las dos secciones no se solapan.
        usersAPI.getLearnedWords({
          limit: HISTORY_PAGE_SIZE,
          offset: 0,
          to: weekFrom - 1,
        }),
      ]);
      setWordsThisWeek(weekRows);
      setWordsAllTime(allFirst);
      setHistoryHasMore(allFirst.length === HISTORY_PAGE_SIZE);
      setHistoryOffset(allFirst.length);
    } catch {
      setWordsThisWeek([]);
      setWordsAllTime([]);
      setHistoryHasMore(false);
      setHistoryOffset(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (segment !== 'learned') return;
    void loadLearnedTab();
  }, [loadLearnedTab, segment]);

  useEffect(() => {
    const seg = (location.state as { learnedWordsSegment?: LearnedWordsSegment } | null)
      ?.learnedWordsSegment;
    if (seg === 'saved' || seg === 'learned') setSegment(seg);
  }, [location.pathname, location.state]);

  const handleLoadMoreHistory = useCallback(async () => {
    if (isLoadingHistoryMore || !historyHasMore) return;
    setIsLoadingHistoryMore(true);
    try {
      const next = await usersAPI.getLearnedWords({
        limit: HISTORY_PAGE_SIZE,
        offset: historyOffset,
        to: getWeekStartMs() - 1,
      });
      setWordsAllTime((prev) => {
        const seen = new Set(prev.map((w) => w.id));
        const merged = [...prev];
        for (const w of next) {
          if (!seen.has(w.id)) {
            seen.add(w.id);
            merged.push(w);
          }
        }
        return merged;
      });
      setHistoryHasMore(next.length === HISTORY_PAGE_SIZE);
      setHistoryOffset((o) => o + next.length);
    } catch {
      setHistoryHasMore(false);
    } finally {
      setIsLoadingHistoryMore(false);
    }
  }, [historyOffset, historyHasMore, isLoadingHistoryMore]);

  // “Todo el historial” muestra todas las palabras, incluidas las de esta
  // semana. La separación entre ambas secciones la garantiza el backend
  // (excludeSeenBefore en /learned-words evita repetir palabras ya vistas
  // en “esta semana”).
  const wordsAllTimeDeduped = wordsAllTime;

  const savedVocabRows = useMemo(
    () => [...savedItems].sort((a, b) => b.savedAt - a.savedAt),
    [savedItems]
  );

  /** Misma división que la pestaña de aprendidas: las dos secciones no se solapan. */
  const savedVocabSplit = useMemo(() => {
    const weekStart = getWeekStartMs();
    const thisWeek = savedVocabRows.filter((s) => s.savedAt >= weekStart);
    const older = savedVocabRows.filter((s) => s.savedAt < weekStart);
    return { thisWeek, older };
  }, [savedVocabRows]);

  const handleRemoveSaved = useCallback(
    async (item: VocabItem) => {
      await removeItem(item.word);
    },
    [removeItem]
  );

  if (!isPeriod(period)) {
    return <Navigate to={ROUTES.STUDENT.PROGRESS} replace />;
  }

  return (
    <div className={styles.page}>
      <Tabs
        value={segment}
        onValueChange={(v) => {
          if (v === 'learned' || v === 'saved') setSegment(v);
        }}
        className={styles.page__tabs}
      >
        <header className={styles.page__header}>
          <SegmentedTabsList
            value={segment}
            items={[
              { value: 'learned', label: t('learnedWordsList.segmentLearned') },
              { value: 'saved', label: t('learnedWordsList.segmentSaved') },
            ]}
            ariaLabel={t('learnedWordsList.sectionsAria')}
          />
        </header>

        <TabsContent value="learned" className={styles.page__tabContent}>
          {loading ? (
            <div className={styles.page__state}>
              <Spinner size="lg" aria-label={t('common.loading')} />
            </div>
          ) : (
            <>
              <section className={styles.page__section} aria-labelledby="learned-section-week">
                <h2 id="learned-section-week" className={styles.page__sectionTitle}>
                  {t('learnedWordsList.sectionThisWeek')}
                </h2>
                {wordsThisWeek.length === 0 ? (
                  <p className={styles.page__sectionEmpty}>{t('learnedWordsList.emptyThisWeek')}</p>
                ) : (
                  <div className={styles.page__list}>
                    {wordsThisWeek.map((w) => {
                      const item = learnedWordToVocabItem(w);
                      const detailHref = buildRoute(ROUTES.STUDENT.PROGRESS_LEARNED_WORD, {
                        period,
                        wordId: w.id,
                      });
                      return (
                        <VocabCard key={w.id} item={item} detailHref={detailHref} showSaveButton={false} />
                      );
                    })}
                  </div>
                )}
              </section>

              <section className={styles.page__section} aria-labelledby="learned-section-all">
                <h2 id="learned-section-all" className={styles.page__sectionTitle}>
                  {t('learnedWordsList.sectionAllTime')}
                </h2>
                {wordsAllTimeDeduped.length > 0 ? (
                  <div className={styles.page__list}>
                    {wordsAllTimeDeduped.map((w) => {
                      const item = learnedWordToVocabItem(w);
                      const detailHref = buildRoute(ROUTES.STUDENT.PROGRESS_LEARNED_WORD, {
                        period,
                        wordId: w.id,
                      });
                      return (
                        <VocabCard key={w.id} item={item} detailHref={detailHref} showSaveButton={false} />
                      );
                    })}
                  </div>
                ) : (
                  <p className={styles.page__sectionEmpty}>
                    {wordsAllTime.length === 0 && !historyHasMore
                      ? t('learnedWordsList.emptyAllTime')
                      : t('learnedWordsList.emptyAllOlderInWeek')}
                  </p>
                )}
                {historyHasMore && (
                  <div className={styles.page__loadMore}>
                    <Button
                      variant="primary"
                      onClick={() => void handleLoadMoreHistory()}
                      disabled={isLoadingHistoryMore}
                    >
                      {isLoadingHistoryMore ? t('common.loading') : t('storiesPage.loadMore')}
                    </Button>
                  </div>
                )}
              </section>
            </>
          )}
        </TabsContent>

        <TabsContent value="saved" className={styles.page__tabContent}>
          {savedLoading ? (
            <div className={styles.page__state}>
              <Spinner size="lg" aria-label={t('common.loading')} />
            </div>
          ) : savedVocabRows.length === 0 ? (
            <div className={styles.page__state}>
              <p>{t('learnedWordsList.savedEmpty')}</p>
            </div>
          ) : (
            <>
              <section className={styles.page__section} aria-labelledby="saved-section-week">
                <h2 id="saved-section-week" className={styles.page__sectionTitle}>
                  {t('learnedWordsList.sectionThisWeek')}
                </h2>
                {savedVocabSplit.thisWeek.length === 0 ? (
                  <p className={styles.page__sectionEmpty}>{t('learnedWordsList.savedEmptyThisWeek')}</p>
                ) : (
                  <div className={styles.page__list}>
                    {savedVocabSplit.thisWeek.map((row) => {
                      const item = savedVocabularyToVocabItem(row);
                      const detailHref = buildRoute(ROUTES.STUDENT.PROGRESS_SAVED_VOCAB, {
                        period,
                        savedId: row.id,
                      });
                      return (
                        <VocabCard
                          key={row.id}
                          item={item}
                          detailHref={detailHref}
                          isSaved
                          showSaveButton
                          saveActionVariant="trash"
                          showTranslation={false}
                          onToggleSave={handleRemoveSaved}
                        />
                      );
                    })}
                  </div>
                )}
              </section>

              <section className={styles.page__section} aria-labelledby="saved-section-all">
                <h2 id="saved-section-all" className={styles.page__sectionTitle}>
                  {t('learnedWordsList.sectionAllTime')}
                </h2>
                {savedVocabSplit.older.length === 0 ? (
                  <p className={styles.page__sectionEmpty}>
                    {t('learnedWordsList.savedEmptyAllOlderInWeek')}
                  </p>
                ) : (
                  <div className={styles.page__list}>
                    {savedVocabSplit.older.map((row) => {
                      const item = savedVocabularyToVocabItem(row);
                      const detailHref = buildRoute(ROUTES.STUDENT.PROGRESS_SAVED_VOCAB, {
                        period,
                        savedId: row.id,
                      });
                      return (
                        <VocabCard
                          key={row.id}
                          item={item}
                          detailHref={detailHref}
                          isSaved
                          showSaveButton
                          saveActionVariant="trash"
                          showTranslation={false}
                          onToggleSave={handleRemoveSaved}
                        />
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

LearnedWordsListPage.displayName = 'LearnedWordsListPage';
